// Arabic Lab → Grammar & rhetoric: pure helpers shared by the app and tests (no imports). The section draws on the
// learner's "Sciences of Language & Logic" modules, found by name wherever they sit in the library: each note is
// placed in a science (naḥw, ṣarf, balāghah or fiqh al-lughah) and a chapter, terms are picked out of what the My
// course reader extracts, and practice is built from the saved terms. Notes are only ever read.
export type Science='nahw'|'sarf'|'balagha'|'lughah'
export const SCIENCES:{id:Science;label:string;arabic:string}[]=[
 {id:'nahw',label:'Naḥw · grammar',arabic:'النَّحْو'},
 {id:'sarf',label:'Ṣarf · morphology',arabic:'الصَّرْف'},
 {id:'balagha',label:'Balāghah · rhetoric',arabic:'الْبَلَاغَة'},
 {id:'lughah',label:'Fiqh al-lughah · philology',arabic:'فِقْه اللُّغَة'},
]
type Notebook={id:string;name:string;parent_id:string|null}
type Lesson={document_id:string;notebook_id:string;title:string;lesson_number:number|null}
export type GrammarNote<L extends Lesson=Lesson>=L&{root_id:string;science:Science;chapter:string}

export const isGrammarModule=(n:Notebook)=>/sciences?\s+of\s+language/i.test(n.name)
/** Top-most notebooks named "Sciences of Language…" (a nested one inside another is covered by its parent). */
export function grammarRoots(notebooks:Notebook[]):string[]{
 const byId=new Map(notebooks.map(n=>[n.id,n]))
 const inside=(n:Notebook)=>{for(let p=n.parent_id?byId.get(n.parent_id):undefined,g=0;p&&g<30;p=p.parent_id?byId.get(p.parent_id):undefined,g++)if(isGrammarModule(p))return true;return false}
 return notebooks.filter(n=>isGrammarModule(n)&&!inside(n)).map(n=>n.id)
}
/** The science a name points to, if it names one. */
export function scienceOf(name:string):Science|undefined{
 if(/bal[aā]gh|maʿ?'?[aā]n[iī]|bay[aā]n|bad[iī]ʿ?'?/i.test(name))return'balagha'
 if(/fiqh\s+al[-\s]?lugh|philolog/i.test(name))return'lughah'
 if(/[sṣ]arf|morpholog/i.test(name)&&!/na[hḥ]w/i.test(name))return'sarf'
 if(/na[hḥ]w|[aā]j[uū]?rr?[uū]m|i[ʿ'`]?r[aā]b|grammar/i.test(name))return'nahw'
 return undefined
}
// "131. Exam for 'An Introduction to the Science of Balāghah' ('Ilm Al-Ma'ānī)" → the first quoted name.
const EXAM=/^\s*\d*[.)]?\s*exam\s+for\s+["'‘“](.+?)["'’”](?=\s|\(|$)/i
/**
 * Places each note of one module in a science and a chapter. Notes in a sub-module (e.g. Nahw › Al-Ajrumiyyah) take
 * the sub-module's name as their chapter and the nearest named science above them. Notes sitting directly in a flat
 * module are split into courses at each "Exam for '…'" note (the exam closes its course); a course is named after its
 * exam. Notes in a flat module not closed by an exam (e.g. "Lesson 1: Introduction to Morphology (Sarf)") are placed by
 * their own title, with the module's parent as their chapter. Notes whose science cannot be told are left out.
 */
export function grammarNotes<L extends Lesson>(notebooks:Notebook[],rootId:string,lessons:L[]):GrammarNote<L>[]{
 const byId=new Map(notebooks.map(n=>[n.id,n]))
 const out:GrammarNote<L>[]=[]
 const flat=lessons.filter(l=>l.notebook_id===rootId).sort((a,b)=>(a.lesson_number??1e9)-(b.lesson_number??1e9)||a.title.localeCompare(b.title,undefined,{numeric:true}))
 let pending:L[]=[]
 for(const l of flat){
  pending.push(l)
  const exam=l.title.match(EXAM)
  if(exam){const chapter=exam[1].trim(),science=scienceOf(chapter);if(science)for(const p of pending)out.push({...p,root_id:rootId,science,chapter});pending=[]}
 }
 const root=byId.get(rootId),parent=root?.parent_id?byId.get(root.parent_id):undefined
 for(const p of pending){const science=scienceOf(p.title);if(science)out.push({...p,root_id:rootId,science,chapter:parent?.name||root?.name||''})}
 for(const l of lessons){
  if(l.notebook_id===rootId)continue
  let science:Science|undefined,chapter=''
  for(let n=byId.get(l.notebook_id),g=0;n&&n.id!==rootId&&g<30;n=n.parent_id?byId.get(n.parent_id):undefined,g++){
   if(!chapter)chapter=n.name
   science??=scienceOf(n.name)
  }
  if(science)out.push({...l,root_id:rootId,science,chapter})
 }
 return out
}

type Item={kind:string;arabic:string;transliteration:string;english:string}
const words=(a:string)=>a.trim().split(/\s+/).filter(Boolean).length
const QURAN_MARKS=/[ۖ-ۭ]/
const SENTENCE_START=/^(he|she|i|we|they|you|it|this|that|these|those|my|his|her|our|your|their|there|and|so|if|whoever|indeed|say|but|when|o|as|or|not|is|are|was|were|do|does|did|which|who|what|how|let|whatever|until)\b/i
const DEFINITION=/(?:^|\s)(?:هُوَ|هِيَ|مَا|عَلَامَاتُ|عَلَامَةً|أَقْسَامُ|وَأَقْسَامُهُ|عِلْمٌ)(?:\s|$)/
/**
 * Whether an extracted item looks like a technical term (or a short definition from a text), to be ticked for saving
 * by default. Example words and sentences ("He wrote", verses quoted as examples) are left unticked; the learner can
 * tick anything.
 */
export function likelyTerm(i:Item){
 // Verses quoted as examples (Qur'anic marks, or a sūrah:āyah reference given as the reading) and questions are left out.
 if(i.kind==='root'||!i.english.trim()||QURAN_MARKS.test(i.arabic)||/\d+:\d+/.test(i.transliteration)||/[?؟]\s*$/.test(`${i.arabic} ${i.english.trim()}`))return false
 const n=words(i.arabic)
 if(n>4)return n<=16&&!!i.transliteration&&DEFINITION.test(i.arabic)
 if(SENTENCE_START.test(i.english.trim()))return false
 return /(?:^|\s)[وَ]?(?:ال|الْ|اَلْ)/.test(i.arabic)||/^(?:al-|ʿ?ilm\b|ʿ?alām|jamʿ|ism\b|fiʿl|ḥarf|bāb|kalām|i[ʿ']r[aā]b|bin[aā]ʾ?)/i.test(i.transliteration.trim())
}

export type Choice<T>={item:T;prompt:string;promptArabic:boolean;options:string[];answer:string;optionsArabic:boolean}
const shuffle=<T,>(list:T[],random:()=>number)=>{const a=[...list];for(let i=a.length-1;i>0;i--){const j=Math.floor(random()*(i+1));[a[i],a[j]]=[a[j],a[i]]}return a}
const clean=(e:string)=>e.replace(/^\*[^*]+\*\s*[–-]\s*/,'').trim()
/**
 * Term ↔ meaning questions. Wrong options come from the same pool (so the same science), never sharing the answer's
 * meaning or Arabic. Every fourth asks for the Arabic from the meaning. Items without three distinct others are skipped.
 */
export function termQuestions<T extends Item>(pool:T[],random:()=>number=Math.random,count=Infinity):Choice<T>[]{
 const usable=pool.filter(i=>i.english.trim()&&i.kind!=='root')
 const out:Choice<T>[]=[]
 usable.forEach((item,n)=>{
  if(out.length>=count)return
  const others=shuffle(usable.filter(x=>clean(x.english).toLowerCase()!==clean(item.english).toLowerCase()&&x.arabic!==item.arabic),random)
   .filter((x,k,a)=>a.findIndex(y=>clean(y.english).toLowerCase()===clean(x.english).toLowerCase()||y.arabic===x.arabic)===k).slice(0,3)
  if(others.length<3)return
  if(n%4===3)out.push({item,prompt:clean(item.english),promptArabic:false,options:shuffle([item,...others].map(x=>x.arabic),random),answer:item.arabic,optionsArabic:true})
  else out.push({item,prompt:item.arabic,promptArabic:true,options:shuffle([item,...others].map(x=>clean(x.english)),random),answer:clean(item.english),optionsArabic:false})
 })
 return out
}

// ── Signs of iʿrāb, as the Ājurrūmiyyah sets them out ──────────────────────────────────────────────────────
export type Case='raf'|'nasb'|'khafd'|'jazm'
export const CASES:Record<Case,{label:string;arabic:string}>={raf:{label:'rafʿ',arabic:'الرَّفْع'},nasb:{label:'naṣb',arabic:'النَّصْب'},khafd:{label:'khafḍ',arabic:'الْخَفْض'},jazm:{label:'jazm',arabic:'الْجَزْم'}}
export type SignRule={case:Case;sign:string;signArabic:string;places:string[]}
export const SIGN_RULES:SignRule[]=[
 {case:'raf',sign:'ḍammah',signArabic:'الضَّمَّة',places:['the singular noun','the broken plural','the sound feminine plural','the imperfect verb with nothing attached to its end']},
 {case:'raf',sign:'wāw',signArabic:'الْوَاو',places:['the sound masculine plural','the five nouns']},
 {case:'raf',sign:'alif',signArabic:'الْأَلِف',places:['the dual']},
 {case:'raf',sign:'nūn',signArabic:'النُّون',places:['the five verbs']},
 {case:'nasb',sign:'fatḥah',signArabic:'الْفَتْحَة',places:['the singular noun','the broken plural','the imperfect verb with nothing attached to its end']},
 {case:'nasb',sign:'alif',signArabic:'الْأَلِف',places:['the five nouns']},
 {case:'nasb',sign:'kasrah',signArabic:'الْكَسْرَة',places:['the sound feminine plural']},
 {case:'nasb',sign:'yāʾ',signArabic:'الْيَاء',places:['the dual','the sound masculine plural']},
 {case:'nasb',sign:'dropping the nūn',signArabic:'حَذْفُ النُّون',places:['the five verbs']},
 {case:'khafd',sign:'kasrah',signArabic:'الْكَسْرَة',places:['the fully declinable singular noun','the fully declinable broken plural','the sound feminine plural']},
 {case:'khafd',sign:'yāʾ',signArabic:'الْيَاء',places:['the five nouns','the dual','the sound masculine plural']},
 {case:'khafd',sign:'fatḥah',signArabic:'الْفَتْحَة',places:['the diptote (al-ism alladhī lā yanṣarif)']},
 {case:'jazm',sign:'sukūn',signArabic:'السُّكُون',places:['the imperfect verb with a sound last letter']},
 {case:'jazm',sign:'dropping (ḥadhf)',signArabic:'الْحَذْف',places:['the imperfect verb with a weak last letter','the five verbs']},
]
/**
 * Which cases' signs the learner's notes have reached, from their Ājurrūmiyyah lesson titles: a title about the
 * signs (ʿalāmāt) of a case unlocks that case. Chapters not yet studied stay locked.
 */
export function casesReached(titles:string[]):Case[]{
 const about=(re:RegExp)=>titles.some(t=>re.test(t)&&/sign|ʿ?'?al[aā]m[aā]t|ʿalāmāt/i.test(t))
 return (([['raf',/raf[ʿ'’]?/i],['nasb',/na[sṣ]b/i],['khafd',/khaf[dḍ]|jarr/i],['jazm',/jazm/i]] as [Case,RegExp][]).filter(([,re])=>about(re)).map(([c])=>c))
}
export type SignQuestion={id:string;prompt:string;options:string[];answer:string;explain:string}
/**
 * Two kinds of question for each reached case: which sign a place takes ("In rafʿ, the sound masculine plural takes…")
 * and where a sign is used ("In naṣb, the yāʾ is the sign in…", wrong options being places of the case's other signs).
 */
export function signQuestions(cases:Case[],random:()=>number=Math.random):SignQuestion[]{
 const out:SignQuestion[]=[]
 for(const c of cases){
  const rules=SIGN_RULES.filter(r=>r.case===c),signs=rules.map(r=>r.sign)
  for(const r of rules){
   const why=`In ${CASES[c].label} (${CASES[c].arabic}), the ${r.sign} (${r.signArabic}) is the sign in ${r.places.join(', ')}.`
   for(const place of r.places)if(signs.length>=2)out.push({id:`sign:${c}:${place}`,prompt:`In ${CASES[c].label}, which sign does ${place} take?`,options:shuffle(signs,random),answer:r.sign,explain:why})
   const elsewhere=[...new Set(rules.filter(o=>o!==r).flatMap(o=>o.places))].filter(p=>!r.places.includes(p))
   if(elsewhere.length>=1){const answer=r.places[Math.floor(random()*r.places.length)];out.push({id:`place:${c}:${r.sign}`,prompt:`In ${CASES[c].label}, the ${r.sign} is the sign in…`,options:shuffle([answer,...shuffle(elsewhere,random).slice(0,3)],random),answer,explain:why})}
  }
 }
 return shuffle(out,random)
}

// ── Which case? From the learner's own sentences ─────────────────────────────────────────────────────────
// Tanwīn is only ever written on a noun, and its vowel shows the state: ـٌ rafʿ, ـً naṣb, ـٍ khafḍ. Sound feminine
// plurals (‑āt) are left out: in naṣb they take the kasrah, so their ending alone does not tell the state. So are the
// shapes where -in is not khafḍ: shortened (manqūṣ) nouns such as قَاضٍ and مُحَامٍ, and ‑ئِذٍ (يَوْمَئِذٍ).
const MARK=/[ً-ْٰ]/g
export type CaseWord={index:number;word:string;case:'raf'|'nasb'|'khafd'}
export function caseOfWord(word:string):CaseWord['case']|undefined{
 const w=word.replace(/[.,،؛:؟?!"«»()]/g,'').replace(/ّ/g,'')
 const bare=w.replace(MARK,'')
 if(!/[ء-ي]/.test(bare)||/ات$/.test(bare))return undefined
 if(/ٌ$/.test(w))return'raf'
 if(/ٍ$/.test(w)){const stem=bare.replace(/^(?:[وفبلك]?ال|[وفبلك])(?=...)/,'');return /ئذ$/.test(bare)||/^[^ا]ا[^ا]$/.test(stem)||/^م[^ا]ا[^ا]$/.test(stem)?undefined:'khafd'}
 if(/ً[اى]?$/.test(w)||/اً$/.test(w))return'nasb'
 return undefined
}
/** The tanwīn-marked nouns of a sentence (two to ten words, no Qur'ānic marks), each with its state. */
export function caseWords(arabic:string):CaseWord[]{
 if(QURAN_MARKS.test(arabic))return[]
 const words=arabic.trim().split(/\s+/).filter(w=>/[ء-ي]/.test(w))
 if(words.length<2||words.length>10)return[]
 return words.flatMap((word,index)=>{const c=caseOfWord(word);return c?[{index,word,case:c}]:[]})
}
export const CASE_SIGN:Record<CaseWord['case'],{tanwin:string;sign:string;mark:string}>={
 raf:{tanwin:'tanwīn al-ḍamm',sign:'ḍammah',mark:'ـٌ'},nasb:{tanwin:'tanwīn al-fatḥ',sign:'fatḥah',mark:'ـً'},khafd:{tanwin:'tanwīn al-kasr',sign:'kasrah',mark:'ـٍ'},
}
export type CaseQuestion<T>={id:string;item:T;words:string[];target:number;answer:CaseWord['case'];explain:string}
/**
 * One question per usable sentence: which state is the marked word in? The word asked about is chosen at random
 * among the sentence's tanwīn nouns. Ids are stable per sentence and word, for scheduling.
 */
export function caseQuestions<T extends {id:string;arabic:string;kind:string}>(items:T[],random:()=>number=Math.random):CaseQuestion<T>[]{
 const out:CaseQuestion<T>[]=[],seen=new Set<string>()
 for(const item of items){
  if(item.kind!=='sentence'&&item.kind!=='phrase')continue
  const key=item.arabic.replace(MARK,'').replace(/\s+/g,' ').trim()
  if(seen.has(key))continue
  const found=caseWords(item.arabic)
  if(!found.length)continue
  seen.add(key)
  const pick=found[Math.floor(random()*found.length)],s=CASE_SIGN[pick.case]
  out.push({id:`case:${item.id}:${pick.index}`,item,words:item.arabic.trim().split(/\s+/).filter(w=>/[ء-ي]/.test(w)),target:pick.index,answer:pick.case,
   explain:`${pick.word.replace(/[.,،؛:؟?!]/g,'')} ends in ${s.tanwin} (${s.mark}): it is in ${CASES[pick.case].label} (${CASES[pick.case].arabic}), shown by the ${s.sign}.`})
 }
 return out
}

// ── Ṣarf: the ten verb forms, as the standard tables give them (on the pattern فعل) ───────────────────────────
export type VerbForm={form:string;past:string;present:string;masdar:string;active:string;meaning:string;example:string;exampleMeaning:string}
export const VERB_FORMS:VerbForm[]=[
 {form:'I',past:'فَعَلَ',present:'يَفْعَلُ / يَفْعِلُ / يَفْعُلُ',masdar:'(varies, learnt with each verb)',active:'فَاعِل',meaning:'the basic meaning of the root',example:'كَتَبَ',exampleMeaning:'he wrote'},
 {form:'II',past:'فَعَّلَ',present:'يُفَعِّلُ',masdar:'تَفْعِيل',active:'مُفَعِّل',meaning:'intensive or causative: doing it much, or making someone do it',example:'عَلَّمَ',exampleMeaning:'he taught'},
 {form:'III',past:'فَاعَلَ',present:'يُفَاعِلُ',masdar:'مُفَاعَلَة / فِعَال',active:'مُفَاعِل',meaning:'doing it with or to another; attempting it',example:'جَاهَدَ',exampleMeaning:'he strove'},
 {form:'IV',past:'أَفْعَلَ',present:'يُفْعِلُ',masdar:'إِفْعَال',active:'مُفْعِل',meaning:'causative: making something happen',example:'أَسْلَمَ',exampleMeaning:'he submitted'},
 {form:'V',past:'تَفَعَّلَ',present:'يَتَفَعَّلُ',masdar:'تَفَعُّل',active:'مُتَفَعِّل',meaning:'reflexive of II: doing it to oneself, gradually',example:'تَعَلَّمَ',exampleMeaning:'he learnt'},
 {form:'VI',past:'تَفَاعَلَ',present:'يَتَفَاعَلُ',masdar:'تَفَاعُل',active:'مُتَفَاعِل',meaning:'mutual: doing it together, to each other',example:'تَعَاوَنَ',exampleMeaning:'he cooperated'},
 {form:'VII',past:'اِنْفَعَلَ',present:'يَنْفَعِلُ',masdar:'اِنْفِعَال',active:'مُنْفَعِل',meaning:'passive or reflexive of I: undergoing it',example:'اِنْكَسَرَ',exampleMeaning:'it broke'},
 {form:'VIII',past:'اِفْتَعَلَ',present:'يَفْتَعِلُ',masdar:'اِفْتِعَال',active:'مُفْتَعِل',meaning:'reflexive: doing it for oneself',example:'اِجْتَمَعَ',exampleMeaning:'he gathered, met'},
 {form:'IX',past:'اِفْعَلَّ',present:'يَفْعَلُّ',masdar:'اِفْعِلَال',active:'مُفْعَلّ',meaning:'colours and physical states: becoming so',example:'اِحْمَرَّ',exampleMeaning:'it turned red'},
 {form:'X',past:'اِسْتَفْعَلَ',present:'يَسْتَفْعِلُ',masdar:'اِسْتِفْعَال',active:'مُسْتَفْعِل',meaning:'seeking or considering: asking for it, deeming so',example:'اِسْتَغْفَرَ',exampleMeaning:'he sought forgiveness'},
]
export type FormQuestion={id:string;prompt:string;promptArabic:string;options:string[];optionsArabic:boolean;answer:string;explain:string}
/** Questions on the ten forms: past → form, form → present, form → maṣdar, example → form, form → meaning. */
export function formQuestions(random:()=>number=Math.random):FormQuestion[]{
 const out:FormQuestion[]=[]
 const others=(f:VerbForm,get:(v:VerbForm)=>string,pool=VERB_FORMS)=>shuffle(pool.filter(v=>v!==f&&get(v)!==get(f)),random).slice(0,3).map(get)
 const about=(f:VerbForm)=>`Form ${f.form}: ${f.past} – ${f.present}, maṣdar ${f.masdar}, active participle ${f.active}. ${f.meaning[0].toUpperCase()+f.meaning.slice(1)}; e.g. ${f.example} (${f.exampleMeaning}).`
 for(const f of VERB_FORMS){
  out.push({id:`form:past:${f.form}`,prompt:'Which form is this pattern?',promptArabic:f.past,options:shuffle([f.form,...others(f,v=>v.form)],random).map(x=>`Form ${x}`),optionsArabic:false,answer:`Form ${f.form}`,explain:about(f)})
  out.push({id:`form:example:${f.form}`,prompt:`Which form is ${f.example} (${f.exampleMeaning})?`,promptArabic:f.example,options:shuffle([f.form,...others(f,v=>v.form)],random).map(x=>`Form ${x}`),optionsArabic:false,answer:`Form ${f.form}`,explain:about(f)})
  if(f.form!=='I'){
   const regular=VERB_FORMS.filter(v=>v.form!=='I')
   out.push({id:`form:present:${f.form}`,prompt:`Form ${f.form} (${f.past}): its present tense?`,promptArabic:'',options:shuffle([f.present,...others(f,v=>v.present,regular)],random),optionsArabic:true,answer:f.present,explain:about(f)})
   out.push({id:`form:masdar:${f.form}`,prompt:`Form ${f.form} (${f.past}): its maṣdar?`,promptArabic:'',options:shuffle([f.masdar,...others(f,v=>v.masdar,regular)],random),optionsArabic:true,answer:f.masdar,explain:about(f)})
  }
  out.push({id:`form:meaning:${f.form}`,prompt:`What does Form ${f.form} (${f.past}) usually add to the root’s meaning?`,promptArabic:'',options:shuffle([f.meaning,...others(f,v=>v.meaning)],random),optionsArabic:false,answer:f.meaning,explain:about(f)})
 }
 return shuffle(out,random)
}
